import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, CheckCircle, Clock, Calendar, Target, Award,
  ClipboardList, Heart, Microscope, Palette, Sprout, ChevronRight,
  Filter, Play, Pause
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyAnalytics = ({ user }) => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [tasks, setTasks] = useState({ assigned_to_me: [], assigned_by_me: [], common: [], counts: {} });
  const [dailyScans, setDailyScans] = useState([]);
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    inProgressTasks: 0
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0]
  });

  const fetchTasks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
      
      // Calculate stats
      const allTasks = [
        ...(response.data.assigned_to_me || []),
        ...(response.data.assigned_by_me || []),
        ...(response.data.common || [])
      ];
      const uniqueTasks = allTasks.filter((task, index, self) => 
        index === self.findIndex(t => t.id === task.id)
      );
      
      const completed = uniqueTasks.filter(t => t.status === 'completed').length;
      const pending = uniqueTasks.filter(t => t.status === 'pending').length;
      const inProgress = uniqueTasks.filter(t => t.status === 'in_progress').length;
      
      setStats({
        totalTasks: uniqueTasks.length,
        completedTasks: completed,
        pendingTasks: pending,
        inProgressTasks: inProgress
      });
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  }, []);

  const fetchDailyScans = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/daily-scans`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          from_date: dateRange.from_date,
          to_date: dateRange.to_date
        }
      });
      setDailyScans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch daily scans:', error);
    }
  }, [dateRange]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTasks(), fetchDailyScans()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTasks, fetchDailyScans]);

  const getStatusColor = (status) => {
    const colors = {
      pending: '#F59E0B',
      in_progress: '#3B82F6',
      completed: '#10B981'
    };
    return colors[status] || '#6B7280';
  };

  const getDimensionIcon = (dimension) => {
    const icons = {
      cness: <ClipboardList size={16} />,
      self_care: <Heart size={16} />,
      rnd: <Microscope size={16} />,
      personal_growth: <Sprout size={16} />,
      creative: <Palette size={16} />
    };
    return icons[dimension] || <ClipboardList size={16} />;
  };

  const getMoodEmoji = (mood) => {
    const moodMap = {
      'very_happy': '😊',
      'happy': '🙂',
      'neutral': '😐',
      'sad': '😔',
      'very_sad': '😢'
    };
    return moodMap[mood] || '😐';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading your analytics...</p>
      </div>
    );
  }

  return (
    <div className="content-area" data-testid="my-analytics-page">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <TrendingUp size={32} color="#7077FE" />
          My Analytics
        </h1>
        <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>Track your tasks and daily activity</p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '20px', background: 'linear-gradient(135deg, #7077FE20, #7077FE10)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Target size={24} color="#7077FE" />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Total Tasks</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#7077FE' }}>{stats.totalTasks}</div>
        </div>
        <div style={{ padding: '20px', background: 'linear-gradient(135deg, #10B98120, #10B98110)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <CheckCircle size={24} color="#10B981" />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Completed</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#10B981' }}>{stats.completedTasks}</div>
        </div>
        <div style={{ padding: '20px', background: 'linear-gradient(135deg, #3B82F620, #3B82F610)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Play size={24} color="#3B82F6" />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>In Progress</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#3B82F6' }}>{stats.inProgressTasks}</div>
        </div>
        <div style={{ padding: '20px', background: 'linear-gradient(135deg, #F59E0B20, #F59E0B10)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Pause size={24} color="#F59E0B" />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Pending</span>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#F59E0B' }}>{stats.pendingTasks}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
        <button
          onClick={() => setActiveTab('tasks')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'tasks' ? '#7077FE' : 'transparent',
            color: activeTab === 'tasks' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-tasks"
        >
          <ClipboardList size={18} />
          My Tasks
        </button>
        <button
          onClick={() => setActiveTab('daily-scans')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'daily-scans' ? '#7077FE' : 'transparent',
            color: activeTab === 'daily-scans' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-daily-scans"
        >
          <Calendar size={18} />
          Daily Scans
        </button>
      </div>

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Tasks Assigned to Me
          </h3>
          {tasks.assigned_to_me?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <ClipboardList size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No tasks assigned to you</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tasks.assigned_to_me?.slice(0, 10).map(task => (
                <div
                  key={task.id}
                  style={{
                    padding: '16px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color)',
                    borderLeft: `4px solid ${getStatusColor(task.status)}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {getDimensionIcon(task.dimension)}
                      <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{task.title}</span>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: `${getStatusColor(task.status)}20`,
                      color: getStatusColor(task.status),
                      textTransform: 'capitalize'
                    }}>
                      {task.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {task.description?.slice(0, 100)}{task.description?.length > 100 ? '...' : ''}
                  </p>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    {task.deadline && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={12} /> Due: {formatDate(task.deadline)}
                      </span>
                    )}
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Priority: {task.priority || 5}/10
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Daily Scans Tab */}
      {activeTab === 'daily-scans' && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              My Daily Scans
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Filter size={16} color="var(--text-tertiary)" />
              <input
                type="date"
                value={dateRange.from_date}
                onChange={(e) => setDateRange({ ...dateRange, from_date: e.target.value })}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '13px'
                }}
              />
              <span style={{ color: 'var(--text-tertiary)' }}>to</span>
              <input
                type="date"
                value={dateRange.to_date}
                onChange={(e) => setDateRange({ ...dateRange, to_date: e.target.value })}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>

          {dailyScans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No daily scans found for this period</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {dailyScans.map(scan => (
                <div
                  key={scan.id}
                  style={{
                    padding: '16px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      {formatDate(scan.date || scan.created_at)}
                    </span>
                    <span style={{ fontSize: '24px' }}>{getMoodEmoji(scan.mood)}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
                    {scan.dimensions && Object.entries(scan.dimensions).map(([key, value]) => (
                      <div key={key} style={{ textAlign: 'center', padding: '8px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'capitalize', marginBottom: '4px' }}>
                          {key.replace('_', ' ')}
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          {value}/10
                        </div>
                      </div>
                    ))}
                  </div>
                  {scan.notes && (
                    <p style={{ margin: '12px 0 0 0', color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>
                      "{scan.notes}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyAnalytics;
